import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { api, classifyTrainType, trainTypeIcons, trainTypeColors, categoryLabels } from './trainUtils';
import '../css/trainSearch.css';

const TrainSearch = ({ onTrainSelect, selectedTrainUUID }) => {
  const [trainLines, setTrainLines] = useState([]);
  const [trainSeries, setTrainSeries] = useState([]);
  const [operatingDate, setOperatingDate] = useState('');
  const [selectedLine, setSelectedLine] = useState(null);
  const [selectedSerie, setSelectedSerie] = useState(null);
  const [trainNumber, setTrainNumber] = useState('');
  const [selectedCategoryType, setSelectedCategoryType] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // Fetch train lines
  useEffect(() => {
    api.get('/v1/train-lines').then(res => {
      setTrainLines(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
  }, []);

  // Fetch train series
  useEffect(() => {
    api.get('/v1/train-series').then(res => {
      setTrainSeries(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
  }, []);

  const lineOptions = useMemo(() =>
    trainLines.map(l => ({ value: l.UUID || l.trainLineUUID, label: l.trainLineName })),
    [trainLines]
  );

  const serieOptions = useMemo(() =>
    trainSeries.map(s => ({ value: s.UUID || s.trainSerieUUID, label: s.trainSerieName || s.trainSeriesName || s.name })),
    [trainSeries]
  );

  const categoryFilters = [
    { type: 'fern', apiValue: 'longdistance' },
    { type: 'sbahn', apiValue: 'localtrain' },
    { type: 'regio', apiValue: 'localtrain' },
    { type: 'ubahn', apiValue: 'metro' },
    { type: 'tram', apiValue: 'tram' },
    { type: 'bus', apiValue: 'bus' },
  ];

  const selectStyles = {
    control: (base) => ({
      ...base,
      background: 'var(--input-bg)',
      borderColor: 'var(--border-color)',
      color: 'var(--text-primary)',
      fontSize: '0.85rem',
      minHeight: '34px',
    }),
    menu: (base) => ({
      ...base,
      background: 'var(--bg-card)',
      border: '1px solid var(--border-color)',
      zIndex: 10,
    }),
    option: (base, state) => ({
      ...base,
      background: state.isFocused ? 'var(--bg-hover)' : 'transparent',
      color: 'var(--text-primary)',
      fontSize: '0.85rem',
    }),
    singleValue: (base) => ({
      ...base,
      color: 'var(--text-primary)',
    }),
    input: (base) => ({
      ...base,
      color: 'var(--text-primary)',
    }),
    placeholder: (base) => ({
      ...base,
      color: 'var(--input-placeholder)',
    }),
  };

  const buildParams = () => {
    const params = {};
    if (operatingDate) params.operatingDate = operatingDate;
    if (selectedLine) params.trainLine = selectedLine.value;
    if (selectedSerie) params.trainSerie = selectedSerie.value;
    if (trainNumber.trim()) params.trainNumber = trainNumber.trim();
    const activeCf = categoryFilters.find(cf => cf.type === selectedCategoryType);
    if (activeCf) params.trainTypeProductCategorie = activeCf.apiValue;
    return params;
  };

  const handleSearch = () => {
    const params = buildParams();
    setLoading(true);
    setSearched(true);
    setResults([]);

    api.get('/v1/trains', { params })
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : [];
        setResults(data);
      })
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  };

  const handleReset = () => {
    setOperatingDate('');
    setSelectedLine(null);
    setSelectedSerie(null);
    setTrainNumber('');
    setSelectedCategoryType('');
    setResults([]);
    setSearched(false);
  };

  return (
    <div className="train-search">
      <h4 className="train-search-title">
        <i className="fa fa-search"></i> Zugsuche
      </h4>

      <div className="train-search-form">
        <div className="search-field">
          <label className="search-label">Betriebstag</label>
          <input
            type="date"
            className="search-input"
            value={operatingDate}
            onChange={e => setOperatingDate(e.target.value)}
          />
        </div>

        <div className="search-field">
          <label className="search-label">Linie</label>
          <Select
            options={lineOptions}
            value={selectedLine}
            onChange={setSelectedLine}
            isClearable
            placeholder="Alle Linien"
            styles={selectStyles}
          />
        </div>

        <div className="search-field">
          <label className="search-label">Baureihe</label>
          <Select
            options={serieOptions}
            value={selectedSerie}
            onChange={setSelectedSerie}
            isClearable
            placeholder="Alle Baureihen"
            styles={selectStyles}
          />
        </div>

        <div className="search-field">
          <label className="search-label">Zugnummer</label>
          <input
            type="text"
            className="search-input"
            value={trainNumber}
            onChange={e => setTrainNumber(e.target.value)}
            placeholder="z.B. 201"
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
          />
        </div>

        <div className="search-field">
          <label className="search-label">Produktkategorie</label>
          <div className="search-category-filters">
            {categoryFilters.map(cf => (
              <button
                key={cf.type}
                className={`type-filter-btn ${selectedCategoryType === cf.type ? 'active' : ''}`}
                onClick={() => setSelectedCategoryType(prev => prev === cf.type ? '' : cf.type)}
                title={categoryLabels[cf.apiValue] || cf.type}
              >
                {trainTypeIcons[cf.type]}
              </button>
            ))}
          </div>
        </div>

        <div className="search-actions">
          <button className="search-btn search-btn-primary" onClick={handleSearch} disabled={loading}>
            {loading ? <><i className="fa fa-spinner fa-spin"></i> Suche...</> : <><i className="fa fa-search"></i> Suchen</>}
          </button>
          <button className="search-btn search-btn-secondary" onClick={handleReset}>
            <i className="fa fa-times"></i> Zurücksetzen
          </button>
        </div>
      </div>

      {/* Results */}
      <div className="train-search-results">
        {!loading && searched && results.length === 0 && (
          <div className="search-empty">
            <i className="fa fa-train"></i>
            <span>Keine Züge gefunden</span>
          </div>
        )}
        {results.map((train, i) => {
          const type = classifyTrainType(train);
          const color = trainTypeColors[type];
          const icon = trainTypeIcons[type];
          const isSelected = (train.UUID || train.trainUUID) === selectedTrainUUID;
          const serie = train.trainSeries?.[0];
          return (
            <div
              key={train.UUID || train.trainUUID || i}
              className={`search-result-card ${isSelected ? 'selected' : ''}`}
              style={{ borderLeft: `4px solid ${color}` }}
              onClick={() => onTrainSelect(train)}
            >
              <div className="search-result-icon">{icon}</div>
              <div className="search-result-info">
                <div className="search-result-name">
                  {train.trainName_long || train.trainName || train.trainTypeName || '?'}
                </div>
                <div className="search-result-meta">
                  {train.operatingDate && <span><i className="fa fa-calendar"></i> {train.operatingDate}</span>}
                  {train.trainLineName && <span>Linie {train.trainLineName}</span>}
                  {train.trainNumber && <span>Nr. {train.trainNumber}</span>}
                  {train.trainTypeVerkehrsmittel && <span>{train.trainTypeVerkehrsmittel}</span>}
                  {serie?.trainSerieName && <span><i className="fa fa-cog"></i> {serie.trainSerieName}</span>}
                  {serie?.trainSerieOperator && <span><i className="fa fa-building"></i> {serie.trainSerieOperator}</span>}
                  {train.trainTerminus && <span><i className="fa fa-map-marker"></i> von {train.trainTerminus}</span>}
                </div>
              </div>
              <div className="search-result-badge" style={{ background: color }}>
                {categoryLabels[train.trainTypeProductCategorie] || train.trainTypeName || type}
              </div>
            </div>
          );
        })}
        {loading && (
          <div className="search-loading">
            <i className="fa fa-spinner fa-spin"></i> Züge werden gesucht...
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainSearch;
